// ── BCHNostr — Video playlist ─────────────────────────────────────
//
// A playlist is a NIP-51 curation set (kind 30005) holding REFERENCES to
// videos — `a` tags pointing at addressable video coordinates. No media is
// duplicated, and removing a video from a playlist doesn't touch the video.
//
// Each referenced video is resolved with its own author filter, so a
// playlist can never cause one creator's `d` tag to be attributed to
// another (see usePlaylist).

import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ListVideo, Play, Trash2, Loader2, Video, AlertTriangle, User,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { usePlaylist, useVideoEngagement, useVideoPublish } from '../hooks/useVideo';
import VideoCard from '../components/VideoCard';
import UserBadges from '../components/UserBadges';
import { VideoEmptyState } from './VideosPage';
import {
  videoCoordinate, videoWatchPath, videoChannelPath, formatVideoDuration,
  type BCHVideo,
} from '../lib/video';

export default function VideoPlaylistPage() {
  const { pubkey: rawPubkey, dTag: rawDTag } = useParams<{ pubkey: string; dTag: string }>();
  const pubkey = useMemo(
    () => (rawPubkey && /^[0-9a-f]{64}$/i.test(rawPubkey) ? rawPubkey.toLowerCase() : undefined),
    [rawPubkey],
  );
  const dTag = useMemo(() => {
    if (!rawDTag) return undefined;
    try { return decodeURIComponent(rawDTag); } catch { return rawDTag; }
  }, [rawDTag]);

  const { user } = useAppContext();
  const { playlist, videos, loading } = usePlaylist(pubkey, dTag);
  const { profile } = useNostrProfile(pubkey);
  const engagement = useVideoEngagement(videos);
  const { savePlaylist, publishing } = useVideoPublish();
  const [removing, setRemoving] = useState('');

  const isOwner = !!user && !!pubkey && user.pubkey === pubkey;
  const name = profile?.display_name || profile?.name || (pubkey ? pubkey.slice(0, 8) : '');

  /** Total runtime, but only if every video reports a duration. */
  const totalDuration = useMemo(() => {
    if (videos.length === 0) return 0;
    if (videos.some(v => v.duration <= 0)) return 0;
    return videos.reduce((sum, v) => sum + v.duration, 0);
  }, [videos]);

  /**
   * Some referenced videos may be missing — deleted by their creator, or
   * simply not present on the relays we're connected to. We say so rather
   * than silently shrinking the playlist.
   */
  const missing = playlist ? playlist.videoRefs.length - videos.length : 0;

  const handleRemove = async (video: BCHVideo) => {
    if (!playlist || !dTag) return;
    const coord = videoCoordinate(video.pubkey, video.id);
    setRemoving(coord);
    try {
      await savePlaylist(dTag, {
        title: playlist.title,
        description: playlist.description,
        image: playlist.image,
        videoRefs: playlist.videoRefs.filter(r => r !== coord),
      });
    } catch {} finally {
      setRemoving('');
    }
  };

  if (!pubkey || !dTag) {
    return <VideoEmptyState title="Playlist not found" body="That playlist link isn’t valid." />;
  }

  if (loading && !playlist) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!playlist) {
    return (
      <VideoEmptyState
        title="Playlist not found"
        body="This playlist isn’t available. It may have been removed by its creator."
        action={{ to: '/videos', label: 'Browse BCH Video' }}
      />
    );
  }

  const first = videos[0];

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-2">
        <Link
          to={videoChannelPath(pubkey)}
          className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-bold truncate flex items-center gap-1.5">
            <ListVideo className="w-4 h-4 text-primary shrink-0" /> {playlist.title}
          </h1>
          <p className="text-[11px] text-muted-foreground truncate">{name}</p>
        </div>
      </div>

      {/* ── Playlist identity ── */}
      <div className="p-4">
        <div className="flex items-start gap-3.5">
          <div className="relative w-32 sm:w-40 aspect-video rounded-xl overflow-hidden bg-muted border border-border shrink-0">
            {playlist.image || first?.thumbnailUrl ? (
              <img
                src={playlist.image || first.thumbnailUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-emerald-500/5">
                <ListVideo className="w-7 h-7 text-primary/40" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-extrabold leading-snug">{playlist.title}</h2>
            <Link
              to={videoChannelPath(pubkey)}
              className="inline-flex items-center gap-1.5 mt-1 text-[11px] text-primary hover:underline"
            >
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
              ) : (
                <User className="w-3 h-3" />
              )}
              <span className="truncate max-w-[10rem]">{name}</span>
              <UserBadges pubkey={pubkey} size="sm" showTooltip={false} />
            </Link>
            <p className="text-[11px] text-muted-foreground mt-1">
              {videos.length} video{videos.length === 1 ? '' : 's'}
              {totalDuration > 0 && ` · ${formatVideoDuration(totalDuration)} total`}
            </p>

            {first && (
              <Link
                to={videoWatchPath(first.pubkey, first.id)}
                className="inline-flex items-center gap-1.5 mt-2.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
              >
                <Play className="w-3.5 h-3.5" /> Play all
              </Link>
            )}
          </div>
        </div>

        {playlist.description && (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-3 leading-relaxed">
            {playlist.description}
          </p>
        )}

        {missing > 0 && (
          <div className="flex items-start gap-2 mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {missing} video{missing === 1 ? '' : 's'} in this playlist couldn't be loaded.
              {' '}They may have been removed by their creator, or aren't on the relays
              you're connected to.
            </p>
          </div>
        )}
      </div>

      {/* ── Videos, in the creator's chosen order ── */}
      {videos.length === 0 ? (
        <div className="text-center py-16 px-6">
          <Video className="w-12 h-12 mx-auto text-muted-foreground/25 mb-3" />
          <p className="text-sm text-muted-foreground">
            None of this playlist's videos are available right now.
          </p>
        </div>
      ) : (
        <div className="border-t border-border">
          {videos.map((video, i) => {
            const coord = videoCoordinate(video.pubkey, video.id);
            return (
              <div
                key={coord}
                className="flex items-start gap-2.5 px-4 py-3 border-b border-border hover:bg-muted/30 transition-colors"
              >
                <span className="text-[11px] font-bold text-muted-foreground w-5 shrink-0 pt-8 text-center tabular-nums">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <VideoCard
                    video={video}
                    variant="row"
                    engagement={engagement.get(coord)}
                  />
                </div>
                {isOwner && (
                  <button
                    onClick={() => handleRemove(video)}
                    disabled={publishing}
                    className="shrink-0 self-center p-1.5 rounded-lg text-muted-foreground hover:text-red-500 disabled:opacity-50"
                    title="Remove from playlist"
                  >
                    {removing === coord
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="px-4 pt-4 text-[10px] text-muted-foreground/70 leading-relaxed">
        Playlists hold references to videos, never copies. Each video keeps streaming from
        its own host.
      </p>
    </div>
  );
}
